import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Checkbox,
  IconButton} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Visibility,
  ThumbUp,
  Share,
  Comment,
  PlayArrow,
  Add,
  Search,
  Notifications,
  Settings,
  MoreVert,
  Edit,
  Delete,
  Archive,
  Download,
  Cancel} from '@mui/icons-material';

// Mobile-specific hooks
import { usePlatform } from '../../hooks/shared/usePlatform';
import { usePullToRefresh } from '../../hooks/mobile/usePullToRefresh';
import { useHapticFeedback } from '../../hooks/mobile/useHapticFeedback';
import { useTouchGestures } from '../../hooks/mobile/useTouchGestures';
import PullToRefresh from './PullToRefresh';
import BottomSheet from './BottomSheet';
import NewClientExperience from '../../services/NewClientExperience';
import { simpleAuth } from '../../services/simpleAuth';

/**
 * Mobile-specific Dashboard component
 * Features: Pull-to-refresh, haptic feedback, touch gestures, mobile-optimized UI, bottom sheets
 */
const MobileDashboard = ({ user }) => {
  const { isMobile } = usePlatform();
  const { hapticButton, hapticSuccess, hapticError } = useHapticFeedback();
  const { handleTouchStart, handleTouchMove, handleTouchEnd, setSwipeCallback } = useTouchGestures();
  
  // Initialize dashboard data from localStorage or fresh metrics
  const getInitialDashboardData = () => {
    const storedData = localStorage.getItem('dashboardData');
    if (storedData) {
      return JSON.parse(storedData);
    }
    // If no stored data, get fresh metrics for new client
    return {
      metrics: NewClientExperience.getFreshMetrics('dashboard'),
      recentPosts: [],
      trends: [
        { metric: 'Followers', change: 0, positive: true },
        { metric: 'Engagement', change: 0, positive: true },
        { metric: 'Reach', change: 0, positive: true },
      ]};
  };

  const [dashboardData, setDashboardData] = useState(getInitialDashboardData());

  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState('overview');
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [bottomSheetVariant, setBottomSheetVariant] = useState('default');
  const [bottomSheetData, setBottomSheetData] = useState({});

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update dashboard data
      setDashboardData(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          followers: prev.metrics.followers + Math.floor(Math.random() * 100),
          engagement: prev.metrics.engagement + (Math.random() - 0.5) * 2}}));
      
      hapticSuccess();
    } catch (error) {
      hapticError();
      console.error('Refresh failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [hapticSuccess, hapticError]);

  // Pull-to-refresh hook
  const pullToRefresh = usePullToRefresh(handleRefresh);

  // Swipe navigation
  useEffect(() => {
    setSwipeCallback((direction) => {
      const views = ['overview', 'analytics', 'content', 'settings'];
      const currentIndex = views.indexOf(currentView);
      
      if (direction === 'left' && currentIndex < views.length - 1) {
        setCurrentView(views[currentIndex + 1]);
        hapticButton(() => {}, 'medium')();
      } else if (direction === 'right' && currentIndex > 0) {
        setCurrentView(views[currentIndex - 1]);
        hapticButton(() => {}, 'medium')();
      }
    });
  }, [setSwipeCallback, currentView, hapticButton]);

  // Handle post selection
  const handlePostSelect = useCallback((postId) => {
    setSelectedPosts(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  }, []);

  // Handle post actions
  const handlePostAction = useCallback((action, postId) => {
    hapticButton(() => {
      switch (action) {
        case 'edit':
          setBottomSheetVariant('form');
          setBottomSheetData({
            title: 'Edit Post',
            post: dashboardData.recentPosts.find(p => p.id === postId)});
          setShowBottomSheet(true);
          break;
        case 'delete':
          setBottomSheetVariant('selection');
          setBottomSheetData({
            title: 'Delete Post',
            message: 'Are you sure you want to delete this post?',
            actions: [
              { id: 'confirm', label: 'Delete', icon: <Delete />, color: 'error' },
              { id: 'cancel', label: 'Cancel', icon: <Cancel /> },
            ]});
          setShowBottomSheet(true);
          break;
        case 'share':
          setBottomSheetVariant('actions');
          setBottomSheetData({
            title: 'Share Post',
            actions: [
              { id: 'facebook', label: 'Share on Facebook', icon: <Share />, color: 'primary' },
              { id: 'twitter', label: 'Share on Twitter', icon: <Share />, color: 'info' },
              { id: 'linkedin', label: 'Share on LinkedIn', icon: <Share />, color: 'primary' },
            ]});
          setShowBottomSheet(true);
          break;
        default:
          console.log('Action:', action, 'Post ID:', postId);
      }
    }, 'medium')();
  }, [hapticButton, dashboardData.recentPosts]);

  // Handle bottom sheet actions
  const handleBottomSheetAction = useCallback((action) => {
    hapticSuccess();
    setShowBottomSheet(false);
    
    // Handle different actions
    switch (action.id) {
      case 'confirm':
        console.log('Confirmed action');
        break;
      case 'facebook':
      case 'twitter':
      case 'linkedin':
        console.log('Sharing on:', action.id);
        break;
      default:
        console.log('Action:', action);
    }
  }, [hapticSuccess]);

  // Speed dial actions
  const speedDialActions = [
    { 
      icon: <Add />, 
      name: 'New Post', 
      action: () => hapticButton(() => {
        setBottomSheetVariant('form');
        setBottomSheetData({ title: 'Create New Post' });
        setShowBottomSheet(true);
      }, 'medium')() 
    },
    { 
      icon: <Search />, 
      name: 'Search', 
      action: () => hapticButton(() => console.log('Search'), 'medium')() 
    },
    { 
      icon: <Notifications />, 
      name: 'Notifications', 
      action: () => hapticButton(() => console.log('Notifications'), 'medium')() 
    },
    { 
      icon: <Settings />, 
      name: 'Settings', 
      action: () => hapticButton(() => console.log('Settings'), 'medium')() 
    },
  ];

  // Metric card component
  const MetricCard = ({ title, value, change, icon, color = 'primary' }) => (
    <Card 
      sx={{ 
        mb: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 2,
        minHeight: 80}}
      onClick={hapticButton(() => console.log(`Clicked ${title}`), 'light')}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {change && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {change > 0 ? (
              <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
            )}
            <Typography 
              variant="caption" 
              color={change > 0 ? 'success.main' : 'error.main'}
              sx={{ fontWeight: 'medium' }}
            >
              {change > 0 ? '+' : ''}{change}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Recent post card with actions
  const RecentPostCard = ({ post }) => (
    <Card 
      sx={{ 
        mb: 1.5,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
        border: selectedPosts.includes(post.id) ? 2 : 1,
        borderColor: selectedPosts.includes(post.id) ? 'primary.main' : 'divider'}}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Checkbox
              checked={selectedPosts.includes(post.id)}
              onChange={() => handlePostSelect(post.id)}
              size="small"
              sx={{ mr: 1 }}
            />
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: 1, 
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2}}>
              {post.type === 'video' ? <PlayArrow /> : <Visibility />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                {post.platform}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {post.type} post
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right', mr: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {post.engagement}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              engagements
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handlePostAction('more', post.id)}
          >
            <MoreVert />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  if (!isMobile) {
    return null; // Only render on mobile
  }

  return (
    <>
      <PullToRefresh onRefresh={handleRefresh}>
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: 'background.default',
            pb: 8, // Space for bottom navigation
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Header */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back, {simpleAuth.forceRefreshCompanyName()}! Here's your social media overview.
            </Typography>
          </Box>

          {/* Metrics Grid */}
          <Box sx={{ p: 2, pt: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MetricCard
                  title="Followers"
                  value={dashboardData.metrics.followers}
                  change={dashboardData.trends[0].change}
                  icon={<Visibility />}
                  color="primary"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricCard
                  title="Engagement"
                  value={`${dashboardData.metrics.engagement}%`}
                  change={dashboardData.trends[1].change}
                  icon={<ThumbUp />}
                  color="secondary"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricCard
                  title="Reach"
                  value={dashboardData.metrics.reach}
                  change={dashboardData.trends[2].change}
                  icon={<Share />}
                  color="success"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricCard
                  title="Impressions"
                  value={dashboardData.metrics.impressions}
                  icon={<Comment />}
                  color="info"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Recent Posts */}
          <Box sx={{ p: 2, pt: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Recent Posts
              </Typography>
              {selectedPosts.length > 0 && (
                <Chip
                  label={`${selectedPosts.length} selected`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            {dashboardData.recentPosts.map((post) => (
              <RecentPostCard key={post.id} post={post} />
            ))}
          </Box>

          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
              <LinearProgress />
            </Box>
          )}

          {/* Speed Dial */}
          <SpeedDial
            ariaLabel="Quick Actions"
            sx={{ position: 'fixed', bottom: 80, right: 16 }}
            icon={<SpeedDialIcon />}
            onClick={hapticButton(() => {}, 'light')}
          >
            {speedDialActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={action.action}
              />
            ))}
          </SpeedDial>
        </Box>
      </PullToRefresh>

      {/* Bottom Sheet */}
      <BottomSheet
        open={showBottomSheet}
        onClose={() => setShowBottomSheet(false)}
        title={bottomSheetData.title}
        variant={bottomSheetVariant}
        actions={bottomSheetData.actions}
        onAction={handleBottomSheetAction}
        loading={isLoading}
      >
        {bottomSheetVariant === 'form' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {bottomSheetData.post ? 'Edit your post content below:' : 'Create a new post:'}
            </Typography>
            {/* Form content would go here */}
            <Typography variant="body1">
              Form fields would be rendered here for post creation/editing.
            </Typography>
          </Box>
        )}
      </BottomSheet>
    </>
  );
};

export default MobileDashboard;
